import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNotifications } from '../context/NotificationContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function VerifyEmail() {
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  
  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  
  useEffect(() => {
    const verifyEmail = async () => {
      const queryParams = new URLSearchParams(location.search);
      const token = queryParams.get('token');
      
      if (!token) {
        showError('Invalid link.', 'Please request a new verification link.', 5000);
        setVerifying(false);
        return;
      }
      
      try {
        console.log('🔍 Verifying email with Firebase Function token:', token);
        
        // Use Firebase Function to verify token and update Firebase Auth
        const functions = getFunctions();
        const verifyEmailFunction = httpsCallable(functions, 'verifyEmailWithToken');
        const result = await verifyEmailFunction({ token });
        
        if (result.data.success) {
          setEmail(result.data.email);
          setSuccess(true);
          setVerifying(false);
          showSuccess('Email verified successfully! Redirecting to login page...');
          

          
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/login?verified=true');
          }, 3000);
        } else {
          throw new Error(result.data.message || 'Verification failed');
        }
        
      } catch (error) {
        console.error('❌ Error verifying email:', error);
        showError('Verification failed.', 'The link may be invalid or expired. Please request a new one.', 5000);
        setVerifying(false);
      }
    };
    
    verifyEmail();
  }, [location, navigate]);
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex flex-col">
      <Header />
      
      <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">
              Email Verification
            </h2>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            {verifying ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4"></div>
                <p className="text-gray-700 font-medium">Verifying your email address...</p>
              </div>
            ) : success ? (
              <div className="text-center py-6 space-y-4">
                <div className="flex justify-center">
                  <div className="rounded-full bg-green-100 p-3">
                    <CheckCircle className="h-12 w-12 text-green-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Email verified successfully!</h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Your email address <span className="font-medium text-primary">{email}</span> has been verified.
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    You will be redirected to the login page shortly.
                  </p>
                </div>
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-semibold rounded-lg shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-150"
                  >
                    Go to Login
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-gray-600">Processing verification...</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
